import * as React from 'react';
import { styled } from '@mui/material/styles';
import Switch, { type SwitchProps } from '@mui/material/Switch';

/**
 * - ふだん: 「オン / オフ」
 * - 翻訳中(Chrome/Edgeのページ翻訳検知): 「ON / OFF」
 *
 * ※ 翻訳中だけ英語にしたいスイッチだけ、translateToEnglishOnTranslate を true にする。
 */

type CustomSwitchProps = SwitchProps & {
  /**
   * true のとき、ブラウザ翻訳中だけラベルを ON/OFF にする
   * false のときは常に onLabel/offLabel を表示
   */
  translateToEnglishOnTranslate?: boolean;

  /**
   * 通常時のラベル（日本語用）
   */
  onLabel?: string;
  offLabel?: string;

  /**
   * 翻訳中のラベル（英語用）
   */
  onLabelTranslated?: string;
  offLabelTranslated?: string;
};

type StyledSwitchOwnerProps = {
  telemaOnLabelCss: string;
  telemaOffLabelCss: string;
};

const escapeForCssContent = (label: string): string => {
  // CSS content: "..." 用の安全なエスケープ
  const s = String(label ?? '');
  const escaped = s.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `"${escaped}"`;
};

const useIsBrowserTranslated = (enabled: boolean): boolean => {
  const [translated, setTranslated] = React.useState(false);

  React.useEffect(() => {
    if (!enabled) {
      setTranslated(false);
      return;
    }

    const html = document.documentElement;

    const detect = () => {
      const classes = Array.from(html.classList);
      const hasTranslatedClass = classes.some((c) => c.startsWith('translated-')); // translated-ltr / translated-rtl
      const hasGoogleTranslateNodes = !!document.querySelector(
        'iframe.goog-te-banner-frame, .goog-te-banner-frame, .skiptranslate'
      );
      setTranslated(hasTranslatedClass || hasGoogleTranslateNodes);
    };

    detect();

    const moHtml = new MutationObserver(detect);
    try {
      moHtml.observe(html, { attributes: true, attributeFilter: ['class'] });
    } catch {
      // noop
    }

    const moBody = new MutationObserver(detect);
    try {
      if (document.body) {
        moBody.observe(document.body, { childList: true, subtree: true });
      }
    } catch {
      // noop
    }

    return () => {
      moHtml.disconnect();
      moBody.disconnect();
    };
  }, [enabled]);

  return translated;
};

const StyledSwitch = styled(Switch, {
  shouldForwardProp: (prop) =>
    prop !== 'telemaOnLabelCss' && prop !== 'telemaOffLabelCss',
})<StyledSwitchOwnerProps>(({ theme, telemaOnLabelCss, telemaOffLabelCss }) => {
  const trackWidth = 56;
  const trackHeight = 24;
  const thumbSize = 20;
  const padding = 1.8;
  const onTranslate = trackWidth - thumbSize - padding * 2;

  return {
    width: trackWidth,
    height: trackHeight,
    padding: 0,
    display: 'inline-flex',
    cursor: 'pointer',

    '& .MuiSwitch-switchBase': {
      padding,
      transform: 'translateX(0)', // OFF: サムは左端
      transition: theme.transitions.create(['transform'], {
        duration: theme.transitions.duration.shortest,
      }),
      '&.Mui-checked': {
        transform: `translateX(${onTranslate}px)`, // ON: サムは右端
        '& + .MuiSwitch-track': {
          backgroundColor: theme.palette.success.main,
          opacity: 1,
        },
      },
    },

    '& .MuiSwitch-thumb': {
      boxSizing: 'border-box',
      width: thumbSize,
      height: thumbSize,
      backgroundColor: theme.palette.common.white,
      boxShadow: theme.shadows[1],
    },

    '& .MuiSwitch-track': {
      borderRadius: trackHeight / 2,
      backgroundColor: theme.palette.grey[400],
      opacity: 1,
      position: 'relative',
      cursor: 'pointer',

      // OFF時ラベル（擬似要素）
      '&:before': {
        content: telemaOffLabelCss,
        position: 'absolute',
        top: '50%',
        left: padding * 14 + 'px',
        transform: 'translateY(-50%)',
        color: theme.palette.common.white,
        fontSize: '0.75rem',
        fontWeight: 500,
        pointerEvents: 'none',
        userSelect: 'none',
      },
    },

    // ON時ラベル（擬似要素）
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track:before': {
      content: telemaOnLabelCss,
      left: 'auto',
      right: padding * 14 + 'px',
    },
  };
});

const CustomSwitch: React.FC<CustomSwitchProps> = ({
  translateToEnglishOnTranslate = false,
  onLabel = 'オン',
  offLabel = 'オフ',
  onLabelTranslated = 'ON',
  offLabelTranslated = 'OFF',
  ...props
}) => {
  const translated = useIsBrowserTranslated(translateToEnglishOnTranslate);

  const effectiveOn = translated ? onLabelTranslated : onLabel;
  const effectiveOff = translated ? offLabelTranslated : offLabel;

  const telemaOnLabelCss = React.useMemo(
    () => escapeForCssContent(effectiveOn),
    [effectiveOn]
  );
  const telemaOffLabelCss = React.useMemo(
    () => escapeForCssContent(effectiveOff),
    [effectiveOff]
  );

  return (
    <StyledSwitch
      {...props}
      telemaOnLabelCss={telemaOnLabelCss}
      telemaOffLabelCss={telemaOffLabelCss}
    />
  );
};

export default CustomSwitch;
